import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const TEXT_ATTRS = ['placeholder', 'title', 'alt', 'aria-label'] as const;
const INPUT_VALUE_TYPES = new Set(['button', 'submit', 'reset']);
const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'IFRAME']);

const normalize = (value: string) => value.replace(/\u00A0/g, ' ').replace(/\s+/g, ' ').trim();
const hasLetters = (value: string) => /[\p{L}]/u.test(value);

function translateWithSpacing(
  value: string,
  isEnglish: boolean,
  translate: (key: string, options?: Record<string, unknown>) => string,
) {
  if (!isEnglish) return value;
  const match = value.match(/^(\s*)([\s\S]*?)(\s*)$/);
  if (!match) return value;

  const [, prefix, core, suffix] = match;
  const lookupKey = normalize(core);
  if (!lookupKey) return value;

  const translated = translate(lookupKey, { defaultValue: lookupKey });
  if (!translated || translated === lookupKey) return value;
  return `${prefix}${translated}${suffix}`;
}

function shouldSkipTextNode(node: Text) {
  const parent = node.parentElement;
  if (!parent) return true;
  if (SKIP_TAGS.has(parent.tagName)) return true;
  return !!parent.closest('[data-i18n-skip="true"]');
}

export default function AutoTranslate() {
  const { i18n, t } = useTranslation();
  const nodeOriginals = useRef(new WeakMap<Node, string>());
  const attrOriginals = useRef(new WeakMap<Element, Map<string, string>>());
  const titleOriginal = useRef<string | null>(null);
  const syncing = useRef(false);
  const currentLanguage = (i18n.language || i18n.resolvedLanguage || 'tr').toLowerCase();
  const isEnglish = currentLanguage.startsWith('en');

  useEffect(() => {
    if (titleOriginal.current === null) {
      titleOriginal.current = document.title;
    }
  }, []);

  useEffect(() => {
    const hasTranslation = (value: string) => {
      const key = normalize(value);
      if (!key) return false;
      return i18n.exists(key);
    };

    const applyTextNodes = (root: ParentNode) => {
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      let current = walker.nextNode() as Text | null;

      while (current) {
        if (!shouldSkipTextNode(current)) {
          const currentValue = current.nodeValue ?? '';
          const normalizedCurrent = normalize(currentValue);

          // Keep dynamic numeric/text counters untouched to avoid breaking animations.
          if (!hasLetters(normalizedCurrent)) {
            current = walker.nextNode() as Text | null;
            continue;
          }

          if (!nodeOriginals.current.has(current)) {
            nodeOriginals.current.set(current, currentValue);
          }

          const original = nodeOriginals.current.get(current) ?? currentValue;
          const next = translateWithSpacing(original, isEnglish, t);
          if (current.nodeValue !== next) {
            current.nodeValue = next;
          }
        }

        current = walker.nextNode() as Text | null;
      }
    };

    const applyAttributes = (root: ParentNode) => {
      if (!(root instanceof Element || root instanceof Document || root instanceof DocumentFragment)) {
        return;
      }

      const elements = root instanceof Element
        ? [root, ...Array.from(root.querySelectorAll('*'))]
        : Array.from(root.querySelectorAll('*'));

      for (const element of elements) {
        let map = attrOriginals.current.get(element);
        if (!map) {
          map = new Map<string, string>();
          attrOriginals.current.set(element, map);
        }

        for (const attr of TEXT_ATTRS) {
          if (!element.hasAttribute(attr)) continue;

          if (!map.has(attr)) {
            map.set(attr, element.getAttribute(attr) || '');
          }

          const original = map.get(attr) || '';
          const next = translateWithSpacing(original, isEnglish, t);
          if (element.getAttribute(attr) !== next) {
            element.setAttribute(attr, next);
          }
        }

        if (element instanceof HTMLInputElement && INPUT_VALUE_TYPES.has(element.type.toLowerCase())) {
          const key = '__value__';
          if (!map.has(key)) {
            map.set(key, element.value);
          }
          const original = map.get(key) || '';
          const next = translateWithSpacing(original, isEnglish, t);
          if (element.value !== next) {
            element.value = next;
          }
        }
      }
    };

    const applyTitle = () => {
      if (titleOriginal.current === null) {
        titleOriginal.current = document.title;
      }

      const currentTitle = document.title;

      if (!isEnglish) {
        if (titleOriginal.current && currentTitle !== titleOriginal.current) {
          document.title = titleOriginal.current;
        }
        return;
      }

      if (hasTranslation(currentTitle)) {
        titleOriginal.current = currentTitle;
      }

      const sourceTitle = titleOriginal.current || currentTitle;
      const translated = translateWithSpacing(sourceTitle, true, t);
      if (translated !== currentTitle) {
        document.title = translated;
      }
    };

    const applyAll = (root: ParentNode = document.body) => {
      if (syncing.current) return;
      syncing.current = true;
      try {
        applyTextNodes(root);
        applyAttributes(root);
        applyTitle();
      } finally {
        syncing.current = false;
      }
    };

    applyAll(document.body);

    const bodyObserver = new MutationObserver((mutations) => {
      if (syncing.current) return;

      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            applyAll(node as Element);
          } else if (node.nodeType === Node.TEXT_NODE && node.parentNode) {
            applyAll(node.parentNode);
          }
        });
      }
    });

    bodyObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    const titleElement = document.querySelector('title');
    let titleObserver: MutationObserver | null = null;

    if (titleElement) {
      titleObserver = new MutationObserver(() => {
        if (syncing.current) return;
        if (!isEnglish) {
          titleOriginal.current = document.title;
          return;
        }

        applyTitle();
      });

      titleObserver.observe(titleElement, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    return () => {
      bodyObserver.disconnect();
      titleObserver?.disconnect();
    };
  }, [isEnglish, t, currentLanguage]);

  return null;
}
